/**
 * Cloud Upload Error Handlers
 *
 * Utility functions for mapping cloud upload error codes to actionable UI.
 * Provides error information with recovery actions where possible.
 */

import { AxiosError } from "axios";
import {
  CloudUploadErrorCode,
  CLOUD_UPLOAD_ERROR_MESSAGES,
  CLOUD_PERMISSION_ERROR_MESSAGES,
} from "src/types/cloudFiles";

// =============================================================================
// Types
// =============================================================================

/**
 * Action types for error recovery.
 */
export type CloudErrorActionType =
  | "navigate" // Navigate to a different page
  | "modal" // Open a modal dialog
  | "retry" // Retry the failed operation
  | "dismiss"; // Just dismiss/acknowledge the error

/**
 * Action to take for error recovery.
 */
export interface CloudErrorAction {
  /** Button label */
  label: string;
  /** Type of action */
  type: CloudErrorActionType;
  /** Additional data for the action (route path, modal props, etc.) */
  payload?: Record<string, unknown>;
}

/**
 * Comprehensive information about a cloud upload error.
 */
export interface CloudErrorInfo {
  /** The error code */
  code: CloudUploadErrorCode;
  /** User-friendly error message */
  message: string;
  /** Severity level for display */
  severity: "error" | "warning" | "info";
  /** Primary recovery action (if any) */
  action?: CloudErrorAction;
  /** Secondary action (e.g., retry) */
  secondaryAction?: CloudErrorAction;
  /** Whether the error can potentially be recovered from */
  isRecoverable: boolean;
  /** Additional help text */
  helpText?: string;
}

/**
 * Context about the failed operation (for better error messages).
 */
export interface CloudErrorContext {
  /** ID of the connection that failed */
  connectionId?: number;
  /** Name of the connection */
  connectionName?: string;
  /** Provider type (sharepoint, filenet, etc.) */
  providerType?: string;
  /** File path that was being uploaded */
  filePath?: string;
  /** File name */
  fileName?: string;
}

// =============================================================================
// Error Info Factory
// =============================================================================

/**
 * Get comprehensive error information for a cloud upload error code.
 *
 * Returns actionable information including:
 * - User-friendly message (contextualized when possible)
 * - Recovery actions (update credentials, retry, etc.)
 * - Whether the error is recoverable
 *
 * @param errorCode - The error code from the backend
 * @param context - Optional context about the failed operation
 * @returns CloudErrorInfo with all relevant error details
 */
export function getCloudErrorInfo(
  errorCode: CloudUploadErrorCode,
  context?: CloudErrorContext,
): CloudErrorInfo {
  switch (errorCode) {
    case "CREDENTIAL_ERROR":
      return {
        code: errorCode,
        message: context?.connectionName
          ? `Credentials for "${context.connectionName}" are invalid or expired.`
          : "Connection credentials are invalid or expired.",
        severity: "warning",
        action: context?.connectionId
          ? {
              label: "Update Credentials",
              type: "modal",
              payload: {
                connectionId: context.connectionId,
                providerType: context.providerType,
              },
            }
          : {
              label: "Go to Connections",
              type: "navigate",
              payload: { path: "/external-connections" },
            },
        secondaryAction: {
          label: "Retry",
          type: "retry",
        },
        isRecoverable: true,
        helpText:
          "Please update the connection credentials and try again. This may happen when passwords expire or certificates are rotated.",
      };

    case "PERMISSION_DENIED":
      return {
        code: errorCode,
        message: context?.filePath
          ? `No permission to access "${context.fileName || context.filePath}".`
          : CLOUD_UPLOAD_ERROR_MESSAGES.PERMISSION_DENIED,
        severity: "error",
        action: {
          label: "Dismiss",
          type: "dismiss",
        },
        isRecoverable: false,
        helpText:
          "Contact your administrator to request access to this file or folder.",
      };

    case "FILE_NOT_FOUND":
      return {
        code: errorCode,
        message: context?.filePath
          ? `File "${context.fileName || context.filePath}" no longer exists.`
          : CLOUD_UPLOAD_ERROR_MESSAGES.FILE_NOT_FOUND,
        severity: "warning",
        action: {
          label: "Refresh",
          type: "retry",
        },
        isRecoverable: true,
        helpText:
          "The file may have been moved or deleted. Try refreshing the file browser.",
      };

    case "DUPLICATE_FILE":
      return {
        code: errorCode,
        message: context?.fileName
          ? `A file named "${context.fileName}" already exists in the dataset.`
          : CLOUD_UPLOAD_ERROR_MESSAGES.DUPLICATE_FILE,
        severity: "warning",
        action: {
          label: "Skip",
          type: "dismiss",
        },
        isRecoverable: true,
        helpText: "You can skip this file or rename it before uploading again.",
      };

    case "CONNECTION_NOT_FOUND":
      return {
        code: errorCode,
        message: context?.connectionName
          ? `Connection "${context.connectionName}" was deleted or is inaccessible.`
          : CLOUD_UPLOAD_ERROR_MESSAGES.CONNECTION_NOT_FOUND,
        severity: "error",
        action: {
          label: "Go to Connections",
          type: "navigate",
          payload: { path: "/external-connections" },
        },
        isRecoverable: false,
        helpText:
          "The connection may have been deleted. Create a new connection to continue.",
      };

    case "UNSUPPORTED_FILE_TYPE":
      return {
        code: errorCode,
        message: context?.fileName
          ? `File type for "${context.fileName}" is not supported.`
          : CLOUD_UPLOAD_ERROR_MESSAGES.UNSUPPORTED_FILE_TYPE,
        severity: "warning",
        action: {
          label: "Dismiss",
          type: "dismiss",
        },
        isRecoverable: false,
        helpText: "Only PDF, Word, Excel, and text files can be imported.",
      };

    case "RATE_LIMIT_EXCEEDED":
      return {
        code: errorCode,
        message: CLOUD_UPLOAD_ERROR_MESSAGES.RATE_LIMIT_EXCEEDED,
        severity: "warning",
        action: {
          label: "Retry Later",
          type: "retry",
        },
        isRecoverable: true,
        helpText: "Too many requests. Please wait a moment and try again.",
      };

    case "DATASET_NOT_FOUND":
      return {
        code: errorCode,
        message: CLOUD_UPLOAD_ERROR_MESSAGES.DATASET_NOT_FOUND,
        severity: "error",
        action: {
          label: "Go to Datasets",
          type: "navigate",
          payload: { path: "/datasets" },
        },
        isRecoverable: false,
        helpText: "The target dataset may have been deleted.",
      };

    case "PROVIDER_NOT_FOUND":
      return {
        code: errorCode,
        message: CLOUD_UPLOAD_ERROR_MESSAGES.PROVIDER_NOT_FOUND,
        severity: "error",
        action: {
          label: "Dismiss",
          type: "dismiss",
        },
        isRecoverable: false,
        helpText:
          "Contact your administrator to configure this cloud provider.",
      };

    case "INTERNAL_ERROR":
    default:
      return {
        code: errorCode,
        message:
          CLOUD_UPLOAD_ERROR_MESSAGES[errorCode] ||
          "An unexpected error occurred.",
        severity: "error",
        action: {
          label: "Retry",
          type: "retry",
        },
        isRecoverable: true,
        helpText: "Please try again. If the problem persists, contact support.",
      };
  }
}

/**
 * Check if an error code indicates a credential-related issue.
 */
export function isCredentialError(errorCode: CloudUploadErrorCode): boolean {
  return errorCode === "CREDENTIAL_ERROR";
}

/**
 * Check if an error is recoverable (user can take action to fix it).
 */
export function isRecoverableError(errorCode: CloudUploadErrorCode): boolean {
  const info = getCloudErrorInfo(errorCode);
  return info.isRecoverable;
}

// =============================================================================
// HTTP 403 Permission Error Utilities
// =============================================================================

/**
 * Check if an error is a permission denied (403) response.
 */
export function isPermissionDeniedError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    return error.response?.status === 403;
  }
  // Also check for generic error objects with response property
  const errorObj = error as { response?: { status?: number } };
  return errorObj?.response?.status === 403;
}

/**
 * Extract permission error detail from 403 response.
 */
export function getPermissionErrorDetail(error: unknown): string | null {
  if (error instanceof AxiosError && error.response?.status === 403) {
    return error.response.data?.detail || null;
  }
  const errorObj = error as {
    response?: { status?: number; data?: { detail?: string } };
  };
  if (errorObj?.response?.status === 403) {
    return errorObj.response.data?.detail || null;
  }
  return null;
}

/**
 * Get user-friendly message for cloud permission errors.
 *
 * Maps the backend permission error (e.g., "Permission denied: cloud:view access required")
 * to a user-friendly message with guidance.
 *
 * @param error - The error from an API call
 * @returns User-friendly error message
 */
export function getCloudPermissionMessage(error: unknown): string {
  const detail = getPermissionErrorDetail(error);

  if (detail?.includes("cloud:view")) {
    return CLOUD_PERMISSION_ERROR_MESSAGES.VIEW_DENIED;
  }
  if (detail?.includes("cloud:use")) {
    return CLOUD_PERMISSION_ERROR_MESSAGES.USE_DENIED;
  }

  return CLOUD_PERMISSION_ERROR_MESSAGES.GENERIC;
}

/**
 * Determine if a 403 error is for cloud:view permission.
 */
export function isCloudViewPermissionError(error: unknown): boolean {
  const detail = getPermissionErrorDetail(error);
  return detail?.includes("cloud:view") ?? false;
}

/**
 * Determine if a 403 error is for cloud:use permission.
 */
export function isCloudUsePermissionError(error: unknown): boolean {
  const detail = getPermissionErrorDetail(error);
  return detail?.includes("cloud:use") ?? false;
}
